import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, ArrowLeft } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";
import { getIndustryBySlug, industryPages } from "../../data/pageConfig";

export default function IndustryPage() {
  const { industrySlug, lang } = useParams();
  const { t } = useTranslation("industries");
  const navigate = useNavigate();
  const industry = getIndustryBySlug(industrySlug);

  if (!industry) {
    navigate(`/${lang || "en"}`, { replace: true });
    return null;
  }

  const Icon = industry.icon;
  const prefix = lang ? `/${lang}` : "";
  const currentLang = lang || "en";

  const painPoints = t(`${industry.key}.painPoints`, { returnObjects: true });
  const solutions = t(`${industry.key}.solutions`, { returnObjects: true });
  const benefits = t(`${industry.key}.benefits`, { returnObjects: true });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `https://trackteam.app/${currentLang}` },
      { "@type": "ListItem", "position": 2, "name": t(`${industry.key}.title`) },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t(`${industry.key}.title`)} — TrackTeam`}
        description={t(`${industry.key}.description`)}
        schema={breadcrumbSchema}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }}></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <Link to={`${prefix}`} className="inline-flex items-center gap-2 text-[#62329f] hover:underline mb-6 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            {t("backToHome")}
          </Link>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${industry.gradient} flex items-center justify-center ${industry.shadowColor} shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
              {t(`${industry.key}.title`)}
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t(`${industry.key}.description`)}
          </p>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {t(`${industry.key}.painPointsTitle`)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Array.isArray(painPoints) &&
              painPoints.map((point, i) => (
                <div key={i} className="bg-red-50 rounded-2xl p-6 border border-red-100">
                  <div className="text-3xl mb-4">{point.emoji || "!"}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{point.title}</h3>
                  <p className="text-gray-600">{point.text}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* How TrackTeam Solves It */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {t(`${industry.key}.solutionsTitle`)}
          </h2>
          <div className="space-y-6">
            {Array.isArray(solutions) &&
              solutions.map((sol, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 flex gap-4 items-start">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${industry.gradient} flex items-center justify-center`}>
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{sol.title}</h3>
                    <p className="text-gray-600">{sol.text}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {t(`${industry.key}.benefitsTitle`)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.isArray(benefits) &&
              benefits.map((benefit, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm">{benefit.text}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Other Industries */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t("otherIndustries")}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {industryPages
              .filter((ind) => ind.slug !== industrySlug)
              .map((ind) => {
                const IIcon = ind.icon;
                return (
                  <Link
                    key={ind.slug}
                    to={`${prefix}/industries/${ind.slug}`}
                    className="flex flex-col items-center gap-3 p-4 rounded-xl bg-white hover:shadow-md transition-all text-center"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${ind.gradient} flex items-center justify-center`}>
                      <IIcon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{t(`${ind.key}.title`)}</span>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#62329f] via-purple-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t(`${industry.key}.ctaTitle`)}</h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">{t(`${industry.key}.ctaSubtitle`)}</p>
          <div className="flex justify-center gap-4">
            <a href="https://apps.apple.com/ae/app/trackteam/id6755401884" target="_blank" rel="noreferrer">
              <img src="/Image/logo_appstore.svg" alt="App Store" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share" target="_blank" rel="noreferrer">
              <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
